import { Component } from '@theme/component';

const kinds = ['helpful', 'like', 'interesting'];
const key = (id, kind = 'helpful') => `${id}:${kind}`;
const widgets = new Set();
const scores = new Map();
const votes = new Map();
const busy = new Set();
const queued = new Set();
const storageKey = 'kunnie:helpful:v1';
let queueScheduled = false;
let memoryVoter = '';
const copy = () => window.kunnieGuideFeedbackCopy || {};
const endpoint = () => `${window.Shopify?.routes?.root || '/'}apps/kunnie/article-helpfulness`;

function voter(create = false) {
  if (memoryVoter) return memoryVoter;
  try { memoryVoter = localStorage.getItem(storageKey) || ''; } catch { /* optional preference storage */ }
  if (!memoryVoter && create) {
    memoryVoter = crypto.randomUUID();
    try { localStorage.setItem(storageKey, memoryVoter); } catch { /* preference lasts for this page */ }
  }
  return memoryVoter;
}

function paint() {
  for (const widget of widgets) widget.paint();
}

function popular() {
  const section = document.querySelector('[data-kunnie-popular]');
  if (!section) return;
  const ranked = [...document.querySelectorAll('.blog-posts-container > [data-article-id]')]
    .filter((card) => (scores.get(key(card.dataset.articleId)) || 0) > 0)
    .sort((a, b) => scores.get(key(b.dataset.articleId)) - scores.get(key(a.dataset.articleId)))
    .slice(0, 3);
  const activeWidget = section.contains(document.activeElement) ? document.activeElement.closest('kunnie-article-vote') : null;
  const active = activeWidget ? key(activeWidget.dataset.articleId, activeWidget.dataset.reaction) : null;
  const list = section.querySelector('[data-popular-list]');
  list.replaceChildren(...ranked.map((card) => {
    const node = card.cloneNode(true);
    node.style.setProperty('--col-span', '2');
    node.removeAttribute('data-blog-index');
    return node;
  }));
  section.hidden = ranked.length === 0;
  if (active) {
    const replacement = [...list.querySelectorAll('kunnie-article-vote')].find((item) => key(item.dataset.articleId, item.dataset.reaction) === active);
    (replacement?.querySelector('button') || document.querySelector(`.blog-posts-container kunnie-article-vote[data-article-id="${activeWidget.dataset.articleId}"][data-reaction="${activeWidget.dataset.reaction}"] button`))?.focus({ preventScroll: true });
  }
}

async function fetchQueued() {
  queueScheduled = false;
  const ids = [...queued];
  queued.clear();
  for (let offset = 0; offset < ids.length; offset += 50) {
    const batch = ids.slice(offset, offset + 50);
    const url = new URL(endpoint(), location.origin);
    url.searchParams.set('ids', batch.join(','));
    if (voter()) url.searchParams.set('voter', voter());
    try {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error('unavailable');
      for (const id of batch) for (const kind of kinds) {
        scores.set(key(id, kind), Number(kind === 'helpful' ? data.counts[id] : data.reactions?.[id]?.[kind]) || 0);
        votes.set(key(id, kind), kind === 'helpful' ? data.mine[id] === true : data.myReactions?.[id]?.includes(kind) === true);
      }
    } catch {
      for (const widget of widgets) if (batch.includes(widget.dataset.articleId)) widget.fail();
    }
  }
  paint();
  popular();
}

class KunnieArticleVote extends Component {
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    widgets.add(this);
    this.querySelector('button')?.addEventListener('click', () => this.vote(), { signal: this.#controller.signal });
    const id = this.dataset.articleId;
    if (scores.has(key(id, this.dataset.reaction))) this.paint();
    else {
      queued.add(id);
      if (!queueScheduled) { queueScheduled = true; queueMicrotask(fetchQueued); }
    }
  }

  disconnectedCallback() {
    widgets.delete(this);
    this.#controller?.abort();
    super.disconnectedCallback();
  }

  paint() {
    const id = key(this.dataset.articleId, this.dataset.reaction);
    const kind = this.dataset.reaction || 'helpful';
    const label = copy()[kind] || copy().helpful;
    const selectedLabel = copy()[kind + '_selected'] || copy().selected;
    const button = this.querySelector('button');
    if (!button || !scores.has(id)) return;
    button.disabled = busy.has(id);
    button.setAttribute('aria-pressed', String(votes.get(id) === true));
    this.querySelector('[data-vote-label]').textContent = votes.get(id) ? selectedLabel : label;
    const count = this.querySelector('[data-vote-count]');
    count.hidden = !scores.get(id);
    count.textContent = String(scores.get(id));
    button.setAttribute('aria-label', `${votes.get(id) ? selectedLabel : label}${scores.get(id) ? ` · ${scores.get(id)}` : ''}`);
  }

  fail() {
    const button = this.querySelector('button');
    if (button) button.disabled = false;
    this.querySelector('[role="status"]').textContent = copy().error || '';
  }

  async vote() {
    const articleId = this.dataset.articleId;
    const reaction = this.dataset.reaction || 'helpful';
    const id = key(articleId, reaction);
    if (busy.has(id)) return;
    busy.add(id); paint();
    this.querySelector('[role="status"]').textContent = '';
    try {
      const response = await fetch(endpoint(), {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ articleId, voter: voter(true), reaction, selected: votes.get(id) !== true }),
        signal: this.#controller.signal,
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error('not_saved');
      scores.set(id, Number(data.count) || 0); votes.set(id, data.selected === true);
      popular();
    } catch (error) {
      if (error.name !== 'AbortError') this.fail();
    } finally { busy.delete(id); paint(); }
  }
}

if (!customElements.get('kunnie-article-vote')) customElements.define('kunnie-article-vote', KunnieArticleVote);
